import AddIcon from "@mui/icons-material/Add";
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useCanModifyUsers } from "../../../hooks/useCanModifyUsers";
import { AdminsAndPermissions } from "../../../pages/Settings/DepartmentConfiguration/forms/AdminsAndPremissions";
import { DEFAULT_ADMIN_FORM_ROW } from "../../../pages/Settings/DepartmentConfiguration/utils";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { useTeamAdmins } from "./useTeamAdmins";

export const TeamAdmins: FC = () => {
	const { userInfo } = useAuth();
	const { t } = useTranslation();
	const hasModifyUserPermission = useCanModifyUsers();
	const {
		existingUsers,
		setExistingUsers,
		originalUsers,
		newUsers,
		setNewUsers,
		isLoading,
		isSaving,
		saveAdminUsers,
		removeUser,
		confirmationDialogOpen,
		handleConfirmSave,
		handleCancelSave,
	} = useTeamAdmins();

	// Parallel keys for newUsers — mutated in lockstep so a row's React key never shifts
	// when peers are added or removed (the flash animation depends on stable keys).
	const newKeyCounter = useRef(0);
	const [newUserKeys, setNewUserKeys] = useState<string[]>([]);

	// `useTeamAdmins` resets newUsers to [] on save; mirror that into the key array.
	useEffect(() => {
		if (newUserKeys.length > newUsers.length) {
			setNewUserKeys((prev) => prev.slice(0, newUsers.length));
		}
	}, [newUsers.length, newUserKeys.length]);

	const [newRowRequestCount, setNewRowRequestCount] = useState(0);

	/**
	 * Drops one draft row, keeping `newUsers` and `newUserKeys` aligned.
	 *
	 * The trim effect above only cuts from the end, so a middle removal there would shift later rows
	 * onto the wrong key and open the wrong panel.
	 */
	const discardNewUser = useCallback(
		(index: number) => {
			setNewUsers((prev) => prev.filter((_, i) => i !== index));
			setNewUserKeys((prev) => prev.filter((_, i) => i !== index));
		},
		[setNewUsers],
	);

	const handleAddNewRow = useCallback(() => {
		newKeyCounter.current += 1;
		const tempKey = `new-${newKeyCounter.current}`;
		// `adminRoles` is re-created per row: a bare spread would alias the constant's array across
		// every new admin, so a later in-place edit would leak between rows.
		setNewUsers((prev) => [...prev, { ...DEFAULT_ADMIN_FORM_ROW, adminRoles: [] }]);
		setNewUserKeys((prev) => [...prev, tempKey]);
		// Tells the grid to open the panel on the new row: the create flow is the panel.
		setNewRowRequestCount((count) => count + 1);
	}, [setNewUsers]);

	if (isLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					p: 4,
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	// One button: editing is per person in the panel, so there is no page-wide Save.
	const actionNodes = hasModifyUserPermission ? (
		<SystemButton
			buttonVariant="primary"
			onClick={handleAddNewRow}
			disabled={isSaving}
			leadingIcon={<AddIcon sx={{ fontSize: "18px" }} />}
			noFullWidthMobile
			sx={{ fontSize: "14px" }}
			data-testid="team-admins-add-new"
		>
			{t("admin.addUser")}
		</SystemButton>
	) : null;

	return (
		<Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2, minHeight: 0 }}>
			<AdminsAndPermissions
				existingUsers={existingUsers}
				setExistingUsers={setExistingUsers}
				originalExistingUsers={originalUsers}
				newUsers={newUsers}
				setNewUsers={setNewUsers}
				newUserKeys={newUserKeys}
				isUpdatingUsers={isSaving}
				onRemoveUser={removeUser}
				currentUserId={userInfo.id}
				actionNodes={actionNodes}
				onSave={saveAdminUsers}
				newRowRequestCount={newRowRequestCount}
				onDiscardNewUser={discardNewUser}
			/>
			<Dialog open={confirmationDialogOpen} onClose={handleCancelSave}>
				<DialogTitle>Confirm Login Details Change</DialogTitle>
				<DialogContent>
					<DialogContentText>This will change the user's login details (email/username or phone number). Are you sure you want to continue?</DialogContentText>
				</DialogContent>
				<DialogActions>
					<SystemButton onClick={handleCancelSave} buttonVariant="secondary">
						Cancel
					</SystemButton>
					<SystemButton onClick={handleConfirmSave} buttonVariant="primary">
						Confirm
					</SystemButton>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
